import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Search, SlidersHorizontal } from "lucide-react";
import { jewelryProducts, categories } from "../data/mockData";

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 35 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const JewelryPage = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const allCategories = ["All", ...new Set(jewelryProducts.map(p => p.category))];

  const filtered = activeCategory === "All"
    ? jewelryProducts
    : jewelryProducts.filter(p => p.category === activeCategory);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center bg-[#1a1a1a]">
        <div className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(https://images.pexels.com/photos/2849742/pexels-photo-2849742.jpeg?auto=compress&w=1920)` }} />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }} className="relative z-10 text-center px-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Our Jewelry Collection
          </h1>
          <div className="h-[1px] w-16 bg-[#c8a96e] mx-auto mt-6" />
        </motion.div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-[#faf9f7] border-b border-[#eee]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap justify-center gap-3">
            {allCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2 text-[11px] tracking-[0.15em] uppercase transition-all duration-300 border ${
                  activeCategory === cat
                    ? "bg-[#1a1a1a] text-white border-[#1a1a1a]"
                    : "bg-transparent text-[#666] border-[#ddd] hover:border-[#c8a96e] hover:text-[#c8a96e]"
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-[#faf9f7]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {filtered.map((product, i) => (
              <FadeIn key={product.id} delay={i * 0.05}>
                <div className="group cursor-pointer">
                  <div className="relative overflow-hidden bg-white aspect-square">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-500" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className="text-white text-[11px] tracking-[0.12em] uppercase"
                        style={{ fontFamily: "'Inter', sans-serif" }}>
                        Inquire Now
                      </span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-[15px] text-[#1a1a1a] group-hover:text-[#c8a96e] transition-colors"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {product.name}
                    </h3>
                    <p className="mt-1 text-[11px] tracking-[0.1em] uppercase text-[#999]"
                      style={{ fontFamily: "'Inter', sans-serif" }}>
                      {product.metal} • {product.stone}
                    </p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[#999] text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                No products found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <h2 className="text-center text-[12px] tracking-[0.3em] uppercase text-[#999] mb-12"
              style={{ fontFamily: "'Inter', sans-serif" }}>
              Browse All Categories
            </h2>
          </FadeIn>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-4">
            {categories.map((cat, i) => (
              <FadeIn key={cat.name} delay={i * 0.03}>
                <div className="group text-center cursor-pointer">
                  <div className="overflow-hidden aspect-square">
                    <img src={cat.image} alt={cat.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy" />
                  </div>
                  <p className="mt-2 text-[10px] tracking-[0.08em] uppercase text-[#555] group-hover:text-[#c8a96e] transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    {cat.name}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default JewelryPage;
